import { useState } from 'react';
import { useI18n } from '../../context/useI18n';
import { resolveOrganizationBrandName } from '../../utils/branding';
import GymOrgManageModal from '../gym/GymOrgManageModal';
import GymImportStructuresPanel from './GymImportStructuresPanel';

const GYM_VIRTUAL_LINKS = [
  {
    id: 'masters',
    titleKey: 'nav.masters',
    fallback: 'Maestros',
    descKey: 'gym.home.masters_desc',
    descFallback: 'Parametriza marca, planes, usuarios, entrenadores y clases de tu sede.',
    ctaKey: 'gym.home.masters_cta',
    ctaFallback: 'Abrir maestros →',
  },
  {
    id: 'adminusers',
    titleKey: 'nav.users',
    fallback: 'Usuarios',
    descKey: 'gym.home.users_desc',
    descFallback: 'Personas de tu sede, accesos y operación diaria.',
  },
  {
    id: 'd28dtracking',
    titleKey: 'nav.tracking',
    fallback: 'Seguimiento',
    descKey: 'gym.home.tracking_desc',
    descFallback: 'Asistencia, participantes y actividad de clases en vivo de tu sede.',
  },
  {
    id: 'gym-attendance',
    titleKey: 'gym.home.attendance',
    fallback: 'Asistencia',
    descKey: 'gym.home.attendance_desc',
    descFallback: 'Registro de asistencia de tus usuarios a las clases.',
  },
  {
    id: 'd28d-gallery',
    titleKey: 'nav.gallery_classes',
    fallback: 'GRABACIONES DE CLASE',
    descKey: 'gym.home.gallery_classes_desc',
    descFallback: 'Grabaciones y enlaces de clases en vivo ya realizadas en tu sede.',
  },
  {
    id: 'gym-mi-marca',
    titleKey: 'nav.mybrand',
    fallback: 'Mi Marca',
    descKey: 'gym.home.branding_desc',
    descFallback: 'Logo, colores, contacto y URL pública.',
    ctaKey: 'gym.home.branding_cta',
    ctaFallback: 'Configurar marca →',
  },
  {
    id: 'modulevigencias',
    titleKey: 'nav.vigencias',
    fallback: 'Licencias',
    descKey: 'gym.home.licenses_desc',
    descFallback: 'Vigencias, pagos pendientes y renovaciones.',
  },
];

const SEDE_LINKS = [
  {
    id: 'adminusers',
    titleKey: 'nav.users',
    fallback: 'Usuarios',
    descKey: 'gym.home.users_desc',
    descFallback: 'Personas de tu sede, habilitar acceso y operación diaria.',
  },
  {
    id: 'd28dtracking',
    titleKey: 'nav.tracking',
    fallback: 'Seguimiento',
    descKey: 'gym.home.tracking_desc',
    descFallback: 'Seguimiento de tus asesorados, participantes y actividad de clases.',
  },
  {
    id: 'liveclasses',
    titleKey: 'nav.liveclasses',
    fallback: 'Clases en vivo',
    descKey: 'gym.home.liveclasses_desc',
    descFallback: 'Calendario de clases programadas (solo lectura).',
  },
  {
    id: 'gym-attendance',
    titleKey: 'gym.home.attendance',
    fallback: 'Asistencia',
    descKey: 'gym.home.attendance_desc',
    descFallback: 'Registro de asistencia de tus usuarios a las clases.',
  },
  {
    id: 'd28d-gallery',
    titleKey: 'nav.gallery_classes',
    fallback: 'GRABACIONES DE CLASE',
    descKey: 'gym.home.gallery_classes_desc',
    descFallback: 'Grabaciones y enlaces de clases en vivo ya realizadas en tu sede.',
  },
  {
    id: 'gym-mi-marca',
    titleKey: 'nav.mybrand',
    fallback: 'Mi Marca',
    descKey: 'gym.home.branding_desc',
    descFallback: 'Logo, colores, contacto y URL pública.',
    ctaKey: 'gym.home.branding_cta',
    ctaFallback: 'Configurar marca →',
  },
  {
    id: 'modulevigencias',
    titleKey: 'nav.vigencias',
    fallback: 'Licencias',
    descKey: 'gym.home.licenses_desc',
    descFallback: 'Vigencias, pagos pendientes y renovaciones.',
  },
];

/** Home operativo del gym admin (sin catálogo de módulos). */
export default function GymAdminHomeCenter({ onNavigate, gymBrand = null, scopeGymId = null, isSedeAdmin = false }) {
  const { t } = useI18n();
  const gymLabel = resolveOrganizationBrandName({ entity: gymBrand });
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const quickLinks = isSedeAdmin ? SEDE_LINKS : GYM_VIRTUAL_LINKS;

  const handleNavigate = (viewId) => {
    if (viewId === 'gym-mi-marca') {
      if (scopeGymId) {
        setBrandModalOpen(true);
        return;
      }
    }
    onNavigate?.(viewId);
  };

  return (
    <div className="dashboard-main-view space-y-6">
      <header>
        <h1 className="d28d-page-title text-2xl mt-1">
          {gymLabel || t('nav.home', 'Inicio')}
        </h1>
        <p className="d28d-text-muted text-sm mt-1">
          {isSedeAdmin
            ? t('gym.home.sede_subtitle', 'Operación de tu sede: asistencia, seguimiento, clases y licencias.')
            : t('gym.home.subtitle', 'Operación de tu sede: usuarios, asistencia, reportes y licencias.')}
        </p>
      </header>
      {!isSedeAdmin && scopeGymId ? (
        <GymImportStructuresPanel scopeGymId={scopeGymId} />
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickLinks.map((link) => (
          <button
            key={link.id}
            type="button"
            className="card p-5 text-left hover:ring-2 hover:ring-lime-400/50 transition"
            onClick={() => handleNavigate(link.id)}
          >
            <span className="font-semibold text-[var(--d28d-text)] block">
              {t(link.titleKey, link.fallback)}
            </span>
            <span className="text-sm d28d-text-muted mt-1 block">
              {t(link.descKey, link.descFallback)}
            </span>
            {link.ctaKey ? (
              <span className="text-sm font-semibold text-lime-700 mt-2 inline-block">
                {t(link.ctaKey, link.ctaFallback)}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <GymOrgManageModal
        scopeGymId={scopeGymId}
        open={brandModalOpen}
        hideArchive
        onClose={() => setBrandModalOpen(false)}
      />
    </div>
  );
}
